import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import EventService from "service/event/EventService";
import usePopover from "components/common/usePopover";
import CreateAppointmentService from "modules/appointments/service/CreateAppointmentService";
import PatientService from "../service/PatientService";
import { confirmDiscardOpenCards } from "./UnsavedCardsGuard";
import "../styles/updateBookingFor.scss";

/**
 * "Update booking for" — the header's switch between the members of the
 * account (Figma 3869:11843 the list, 3869:11856 the confirmation).
 *
 * The list is the ACCOUNT HOLDER's family: the appointment's patientID is the
 * holder, and /v1/family/{holder}?includeExtended=true is the list the server
 * itself checks bookedFor against on create ("family member not match"). The
 * holder is never in that list, so they are added as "Self" — exactly how the
 * create-appointment "Booking for" field builds it. `secondary` (people who
 * added this patient) is not bookable under this account and is ignored, as it
 * is there.
 *
 * The member currently on screen is left out: picking it would change nothing.
 * Everyone else is ALWAYS listed — a record with no name reads "PID n", and a
 * holder whose record can't be read is still offered as "PID n (Self)". A
 * member missing from this list is a switch that cannot be undone.
 * Fetched on open, not on mount — the header renders on every patient page and
 * this list is only needed by someone about to switch.
 */

const nameOf = (p) => [p && p.firstName, p && p.lastName].filter(Boolean).join(" ").trim();

/* The open-card warning's words for this action (UnsavedCardsGuard). The switch
   remounts the screen, which discards any editor card still open. */
const DISCARD_COPY = { what: "Changing who this appointment is booked for", confirmLabel: "Ignore and change" };

/** Figma's close glyph (asset b3e36dc5…, both boards), path verbatim. */
const CloseX = () => (
  <svg viewBox="0 0 28 28" width="28" height="28" fill="none" aria-hidden="true">
    <path d="M8.5 8.5L19.5 19.5M19.5 8.5L8.5 19.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/**
 * The pickable members, labelled "Name (Relation)" as the board draws them.
 * Exported for the harness's sake only through behaviour — kept pure so the
 * rules read in one place: holder first as Self, then the family in the
 * server's order, deduped by patient id, minus whoever is on screen.
 */
export const buildMembers = ({ holderId, holder, includeHolder, families, relations, currentMemberId }) => {
  const relLabel = (code) =>
    Number(code) === 0
      ? "Self"
      : ((relations || []).find((r) => Number(r.code) === Number(code)) || {}).value || "";
  const rows = [];
  const seen = new Set();
  const push = (id, name, relation) => {
    const n = Number(id);
    if (!(n > 0) || seen.has(n)) return;
    seen.add(n);
    const shown = name || `PID ${n}`;
    const rel = relLabel(relation);
    rows.push({ memberPatientId: n, name: shown, relation, label: rel ? `${shown} (${rel})` : shown });
  };
  if (includeHolder) push(holderId, nameOf(holder), 0);
  (families || []).forEach((m) => {
    if (m && Number(m.memberPatientId) !== Number(holderId)) push(m.memberPatientId, nameOf(m), m.relation);
  });
  return rows.filter((r) => r.memberPatientId !== Number(currentMemberId));
};

const UpdateBookingFor = ({ appointment, currentMemberId, onConfirm, blockedReason }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | ok | error
  /* The member picked from the list, awaiting "Confirm change". */
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  savingRef.current = saving;
  /* The open-card warning is up for this confirmation. */
  const askingRef = useRef(false);

  const famService = useRef(new CreateAppointmentService()).current;
  const patientService = useRef(new PatientService()).current;
  const mounted = useRef(true);
  const gen = useRef(0);
  useEffect(() => () => { mounted.current = false; }, []);

  const close = useCallback(() => setOpen(false), []);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, close, `${state}:${rows ? rows.length : 0}`);

  const holderId = Number(appointment && appointment.patientID);

  /* Held (a closed appointment, or a call ringing / live): anything already open
     closes — except a save in flight, which the owner re-checks and refuses. */
  useEffect(() => {
    if (!blockedReason) return;
    setOpen(false);
    if (!savingRef.current) setPending(null);
  }, [blockedReason]);

  const load = useCallback(() => {
    const my = ++gen.current;
    setState("loading");
    /* The holder's own record is only needed to NAME the "Self" row, and that
       row only exists when someone other than the holder is on screen. */
    const needHolder = holderId > 0 && holderId !== Number(currentMemberId);
    let families = null;
    let relations = [];
    let holder = null;
    let failed = false;
    let pendingCalls = needHolder ? 3 : 2;
    /* Only the FAMILY list can fail the load. The holder's record just names
       the "Self" row, which is offered either way ("PID n" without it). */
    const settle = () => {
      pendingCalls -= 1;
      if (pendingCalls > 0 || !mounted.current || my !== gen.current) return;
      if (failed) {
        setState("error");
        return;
      }
      setRows(buildMembers({ holderId, holder, includeHolder: needHolder, families, relations, currentMemberId }));
      setState("ok");
    };
    famService.getFamilyMembers(
      holderId,
      (res) => {
        families = (res && res.families) || [];
        settle();
      },
      () => {
        failed = true;
        settle();
      }
    );
    /* Relation labels are decoration: without them a row is just the name. */
    famService.getRelations(
      (res) => {
        relations = Array.isArray(res) ? res : [];
        settle();
      },
      () => settle()
    );
    if (needHolder) {
      patientService.fetchPatient(
        holderId,
        (p) => {
          if (p && p.id) holder = p;
          settle();
        },
        () => settle()
      );
    }
  }, [holderId, currentMemberId, famService, patientService]);

  useEffect(() => {
    if (open && (state === "idle" || state === "error")) load();
    // A failed load is retried by re-opening (or the Retry link), never in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (row) => {
    setOpen(false);
    setPending(row);
  };

  /* ---- the confirmation: modal, Escape and the backdrop cancel ---- */
  const panelRef = useRef(null);
  useEffect(() => {
    if (!pending) return undefined;
    /* Focus goes back to the trigger, not to whatever had it when this opened —
       by then the row that was clicked has already been unmounted. */
    const returnTo = triggerRef.current;
    /* Capture phase, so an Escape meant for this dialog is not also seen by a
       page-level listener underneath it. */
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (!savingRef.current) setPending(null);
        return;
      }
      /* Modal means modal: Tab cycles inside the panel (the codebase's Dialog
         does the same), so nothing under the backdrop can be reached — least of
         all a navigation while the save is in flight. */
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll("button:not([disabled])");
      if (!focusable.length) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const inside = panelRef.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    /* Focus the panel, not a button: the primary action is what Enter would
       fire, and this one replaces the record on screen. */
    if (panelRef.current) panelRef.current.focus();
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = overflow;
      if (returnTo && returnTo.focus) returnTo.focus();
    };
    // triggerRef is a ref (stable); only an open/close of the dialog re-runs this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const body = () => {
    if (state === "loading" || state === "idle") {
      return <p className="ubf-msg">Loading family members…</p>;
    }
    if (state === "error") {
      return (
        <p className="ubf-msg is-error">
          Couldn’t load family members.{" "}
          <button type="button" className="ubf-retry" onClick={load}>
            Retry
          </button>
        </p>
      );
    }
    if (!rows || rows.length === 0) {
      return <p className="ubf-msg">No other family members on this account</p>;
    }
    return (
      <div className="ubf-list" role="listbox" aria-label="Family members">
        {rows.map((r) => (
          <button
            key={r.memberPatientId}
            type="button"
            role="option"
            aria-selected="false"
            className="ubf-row"
            onClick={() => pick(r)}
          >
            {r.label}
          </button>
        ))}
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`ph-update-booking${blockedReason ? " is-blocked" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        /* HELD, not disabled: a disabled button can't be clicked, and the click
           is where the reason is shown (a toast), as well as on hover (title). */
        aria-disabled={blockedReason ? "true" : undefined}
        title={blockedReason || undefined}
        disabled={!(holderId > 0)}
        onClick={() => {
          if (blockedReason) {
            EventService.emit("showError", { message: blockedReason });
            return;
          }
          setOpen((o) => !o);
        }}
      >
        Update booking for
      </button>

      {open &&
        ReactDOM.createPortal(
          /* Portalled for the same reason as the documents menu beside it:
             `.ph-fold-inner` is overflow:hidden for the collapse animation. */
          <div
            ref={menuRef}
            className="ubf-pop"
            style={{ top: menuStyle.top, left: menuStyle.left, maxHeight: menuStyle.maxHeight }}
            role="dialog"
            aria-label="Update booking for"
          >
            <div className="ubf-head">
              <span className="ubf-title">Update booking for</span>
              <button
                type="button"
                className="ubf-close"
                aria-label="Close"
                onClick={() => {
                  close();
                  if (triggerRef.current) triggerRef.current.focus();
                }}
              >
                <CloseX />
              </button>
            </div>
            {body()}
          </div>,
          document.body
        )}

      {pending &&
        ReactDOM.createPortal(
          <div
            className="ubf-backdrop"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !saving) setPending(null);
            }}
          >
            <div
              ref={panelRef}
              className="ubf-confirm"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ubf-confirm-title"
              tabIndex={-1}
            >
              <div className="ubf-head">
                <h2 id="ubf-confirm-title" className="ubf-title is-confirm">
                  Confirm change
                </h2>
                <button type="button" className="ubf-close" aria-label="Close" onClick={() => setPending(null)} disabled={saving}>
                  <CloseX />
                </button>
              </div>
              <p className="ubf-confirm-body">
                This appointment will be booked for {pending.label}. Patient details on this record will
                change once you confirm.
              </p>
              <div className="ubf-actions">
                <button type="button" className="ubf-btn" onClick={() => setPending(null)} disabled={saving}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="ubf-btn is-primary"
                  disabled={saving}
                  onClick={() => {
                    /* The owner saves the mapping and remounts the screen, so on
                       success this component is gone; on failure it has said
                       why, and the dialog stays for another try. The remount
                       discards any open editor card, so that is asked about
                       FIRST: Cancel there saves nothing (product, 2026-10-09). */
                    if (askingRef.current) return;
                    askingRef.current = true;
                    const member = pending;
                    confirmDiscardOpenCards(DISCARD_COPY).then((go) => {
                      askingRef.current = false;
                      if (!go || !mounted.current) return;
                      setSaving(true);
                      Promise.resolve(onConfirm(member)).then(
                        () => {
                          if (mounted.current) {
                            setSaving(false);
                            setPending(null);
                          }
                        },
                        () => {
                          if (mounted.current) setSaving(false);
                        }
                      );
                    });
                  }}
                >
                  {saving ? "Saving…" : "Confirm change"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

UpdateBookingFor.propTypes = {
  /** Needs `patientID` (the account holder). */
  appointment: PropTypes.object.isRequired,
  /** The patient id of the record on screen — left out of the list. */
  currentMemberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * ({ memberPatientId, name, relation, label }) => Promise, on "Confirm change".
   * Resolve once saved; reject (after saying why) to keep the dialog open.
   */
  onConfirm: PropTypes.func.isRequired,
  /** When set, the button is held (looks disabled) and this is why — shown on
   *  hover and, as a toast, on click. */
  blockedReason: PropTypes.string,
};

export default UpdateBookingFor;
